import React, { PropsWithChildren, useCallback, useRef } from 'react';
import isEmpty from 'lodash-es/isEmpty';
import { FilterOptionsState, Autocomplete } from '@material-ui/lab';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import Chip from '@material-ui/core/Chip';
import { Popper, PopperProps } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';

import isMobileOrTablet from 'app/shared/helpers/isMobileOrTablet';
import isSafariOrIOS from 'app/shared/helpers/isSafariOrIos';
import { LabelEnum } from 'app/shared/enums';
import { DictionaryEntry } from 'app/services/dict.service';
import { UtilService } from 'app/services/util.service';
import { ControlContainer } from 'app/components/ControlContainer';
import { useMultipleSelectScroll } from './useMultipleSelectScroll';
import { useMultipleSelectGroups } from './useMultipleSelectGroups';
import { MultipleSelectProps } from './MultipleSelect.interfaces';
import { useStyles } from './MultipleSelect.styles';

export default function MultipleSelect(props: MultipleSelectProps) {
  const classes = useStyles();

  const {
    answerGroups,
    dependsOn,
    dependsOnFieldValue,
    dictionary,
    disabled,
    handleChange,
    onValueChange,
    onControlBlur,
    helperText,
    label,
    subLabel,
    tooltip,
    width,
    input,
    meta,
    meta: { touched, valid },
  } = props;

  const {
    handleScroll,
    scrollFieldAboveKeyboard,
    resetKeyboardScroll,
  } = useMultipleSelectScroll();
  const formControlRef = useRef<HTMLDivElement>(null);
  const { suggestions, groupBy } = useMultipleSelectGroups(
    dictionary ? dictionary : [],
    answerGroups
  );

  const selectHandleChange = (
    e: React.ChangeEvent,
    entries: Array<DictionaryEntry>
  ) => {
    const entryKeys = entries.map((entry) => entry.key as string);

    handleChange(entryKeys);

    if (onValueChange) {
      onValueChange(entryKeys, true, false);
    }
  };

  const selectHandleBlur = () => {
    if (onControlBlur) {
      onControlBlur(false);
    }
  };

  const hasDictionaryWithoutData = () => isEmpty(suggestions);

  const selectedDictionaryEntries =
    Array.isArray(input.value) && !hasDictionaryWithoutData()
      ? input.value.map((val: string) =>
          suggestions.find((entry) => entry.key === val)
        )
      : [];

  const isDisabled = () =>
    !!(disabled || hasDependsOnWithoutValue() || hasDictionaryWithoutData());

  const hasDependsOnWithoutValue = () =>
    dependsOn && !UtilService.hasValue(dependsOnFieldValue);

  const CustomPopper = useCallback(
    (props: PropsWithChildren<PopperProps>) => (
      <Popper
        {...props}
        placement="bottom-start"
        modifiers={{
          flip: {
            enabled: false,
          },
        }}
        container={() =>
          document.querySelector('.modal-opened') || document.body
        }
        aria-label={'select options'}
      />
    ),
    []
  );

  const filterPolishSigns = (
    options: DictionaryEntry[],
    state: FilterOptionsState<DictionaryEntry>
  ) =>
    options.filter((option: DictionaryEntry) =>
      UtilService.escapeDiacritics(option.value)
        .toLowerCase()
        .includes(UtilService.escapeDiacritics(state.inputValue).toLowerCase())
    );

  return (
    <FormControl
      ref={formControlRef}
      variant="outlined"
      className={classes.formControl}
      disabled={isDisabled()}
      onClick={
        isMobileOrTablet() && isSafariOrIOS()
          ? undefined
          : (e) => handleScroll(e)
      }
      onFocus={() => scrollFieldAboveKeyboard(formControlRef.current)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          resetKeyboardScroll();
        }
      }}
    >
      <ControlContainer
        withoutHtmlFor
        inputId={input.name}
        tooltip={tooltip}
        width={width}
        meta={meta}
        disabled={isDisabled()}
        helperText={helperText}
        label={subLabel ? `${label} - ${subLabel}` : label}
        hoverTooltipText={
          hasDependsOnWithoutValue() || hasDictionaryWithoutData()
            ? LabelEnum.FillPreviousQuestions
            : ''
        }
      >
        <label htmlFor={input.name} style={{ display: 'none' }}>
          {subLabel ? `${label} - ${subLabel}` : label}
        </label>
        <Autocomplete
          openOnFocus
          filterOptions={filterPolishSigns}
          PopperComponent={CustomPopper}
          classes={{
            inputRoot: classes.input,
            input: classes.inputChooseNew,
            popper: classes.popper,
            listbox: classes.listbox,
          }}
          className={classes.autocomplete}
          clearText={LabelEnum.Clear}
          closeText={LabelEnum.Close}
          getOptionLabel={(option: DictionaryEntry) => {
            return option ? option.value : '';
          }}
          groupBy={groupBy}
          id={input.name}
          multiple
          getOptionSelected={(option, value) => option?.key === value?.key}
          onBlur={selectHandleBlur}
          onChange={selectHandleChange}
          options={suggestions}
          noOptionsText={LabelEnum.NoOptions}
          openText={LabelEnum.Open}
          renderInput={(params) => (
            <TextField
              {...params}
              id={input.name}
              error={touched && !valid}
              variant="outlined"
              className={classes.typeField}
              fullWidth
              placeholder={LabelEnum.TypeOrChoose}
            />
          )}
          renderTags={(value: DictionaryEntry[], getTagProps) => {
            return value.map((option: DictionaryEntry, index: number) => (
              <Chip
                deleteIcon={
                  <CancelIcon
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (
                        e.target === e.currentTarget &&
                        (e.key === 'Enter' || e.key === ' ')
                      ) {
                        e.preventDefault();
                        //@ts-ignore
                        getTagProps({ index }).onDelete?.(e);
                      }
                    }}
                  />
                }
                key={option.key}
                color="primary"
                label={option.value}
                classes={{
                  root: classes.chipRoot,
                  deleteIcon: classes.chipDeleteIcon,
                }}
                {...getTagProps({ index })}
              />
            ));
          }}
          value={selectedDictionaryEntries}
        />
      </ControlContainer>
    </FormControl>
  );
}
